import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Footer.css';
import { isSafariBrowser, scrollReveal } from '../utils/safariMotion';

const Footer: React.FC = () => {
  const safari = isSafariBrowser();
  const { t } = useTranslation('common');

  const footerSections = [
    {
      title: t('footerProduct'),
      links: [
        { name: t('footerPricing'), path: '/pricing' },
        { name: t('footerLanguages'), path: '/languages' },
        { name: t('footerCommunity'), path: 'https://discord.gg/guxmb6NERA' },
      ],
    },
    {
      title: t('footerSupport'),
      links: [
        { name: t('footerFAQ'), path: '/faq' },
        { name: t('footerBugReport'), path: '/bugreport' },
        { name: t('footerContact'), path: 'mailto:support@codec422.com' },
        {
          name: t('footerManageBilling'),
          path: 'https://billing.stripe.com/p/login/7sYfZh3Oh9p8fNa4EienS00',
        },
        {
          name: t('footerManageDevices'),
          path: 'https://codec422.com/portal',
        },
      ],
    },
    {
      title: t('footerLegal'),
      links: [
        { name: t('footerPrivacy'), path: 'https://codec422.com/privacy' },
        { name: t('footerCookies'), path: '/cookie-preferences' },
        { name: t('footerTerms'), path: 'https://codec422.com/terms' },
        { name: t('footerLicense'), path: 'https://codec422.com/eula' },
      ],
    },
  ];

  return (
    <footer id="contact" className="footer">
      <div className="footer-container">
        <motion.div
          className="footer-content"
          transition={{ duration: 0.8, ease: 'easeOut' }}
          {...scrollReveal(safari, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, 'section')}
        >
          <motion.div
            className="footer-section"
            transition={{ duration: 0.6, delay: 0.2 }}
            {...scrollReveal(safari, { opacity: 0, x: -20 }, { opacity: 1, x: 0 }, 'nested')}
          >
            <motion.div
              className="footer-logo"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <span className="logo-text">Substyle</span>
            </motion.div>
            <p className="footer-description">{t('footerDescription')}</p>
          </motion.div>

          {footerSections.map((section, index) => (
            <motion.div
              key={section.title}
              className="footer-section"
              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
              {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}
            >
              <h4 className="footer-title">{section.title}</h4>
              <ul className="footer-links">
                {section.links.map((link, linkIndex) => (
                  <motion.li
                    key={linkIndex}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    {link.path.startsWith('mailto:') ? (
                      <a href={link.path}>{link.name}</a>
                    ) : link.path.startsWith('http') ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link to={link.path}>{link.name}</Link>
                    )}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="footer-bottom"
          transition={{ duration: 0.6, delay: 0.8 }}
          {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}
        >
          <p>{t('footerCopyright', { year: 2025 })}</p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
